import React, { useState, useMemo, useEffect } from "react";
import MainLayout from "../components/layout/MainLayout";
import initialTasksData from "../data/tasks.json";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  X
} from "lucide-react";

export type TaskCategory = "all" | "tracking" | "domains" | "hosting" | "accounting" | "infrastructure" | "manual";
export type TaskPriority = "urgent" | "high" | "medium" | "low";
export type TaskStatus = "pending" | "snoozed" | "completed" | "dismissed";

export interface TaskItem {
  id: string;
  category: "domains" | "hosting" | "accounting" | "infrastructure" | "tracking" | "manual";
  title: string;
  description: string;
  priority: TaskPriority;
  dueDate?: string;
  daysLeft?: number;
  source: string;
  provider?: string;
  asset?: string;
  actionType?: string;
  savingsEstimate?: string;
  checklist?: string[];
  links?: { label: string; url: string }[];
  status: TaskStatus;
  completedAt?: string;
  snoozedUntil?: string;
  createdAt: string;
  userNotes?: string;
}

const STORAGE_KEY = "afima_action_center_tasks_v1";
const CHECKLIST_KEY = "afima_action_checklist_v1";

const CATEGORY_LABELS: Record<Exclude<TaskCategory, "all">, string> = {
  tracking: "Tracking",
  domains: "Domains",
  hosting: "Hosting",
  accounting: "Accounting",
  infrastructure: "Infrastructure",
  manual: "General"
};

const PRIORITY_ORDER: Record<TaskPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

// ─── Date helpers (dates are YYYY-MM-DD, read as local calendar days) ───
const DAY_MS = 86_400_000;
const todayStr = () => new Date().toLocaleDateString("en-CA");
const parseDay = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
};
const daysUntil = (s: string) => Math.round((parseDay(s) - parseDay(todayStr())) / DAY_MS);
const shortDate = (s: string) =>
  new Date(parseDay(s)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(s.slice(0, 4) !== todayStr().slice(0, 4) ? { year: "numeric" } : {})
  });
const addDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-CA");
};

function dueLabel(dueDate: string): { text: string; tone: "overdue" | "soon" | "normal" } {
  const n = daysUntil(dueDate);
  if (n < 0) return { text: `Overdue · ${shortDate(dueDate)}`, tone: "overdue" };
  if (n === 0) return { text: "Due today", tone: "soon" };
  if (n === 1) return { text: "Due tomorrow", tone: "soon" };
  if (n <= 7) return { text: `Due ${shortDate(dueDate)} · in ${n} days`, tone: "soon" };
  return { text: `Due ${shortDate(dueDate)}`, tone: "normal" };
}

// Merge the shipped task list with what this browser has saved: shipped metadata wins,
// the user's status/notes are kept, a task shipped as completed stays completed,
// and snoozes that have run out wake up again.
function mergeTasks(shipped: TaskItem[], saved: TaskItem[] | null): TaskItem[] {
  const savedMap = new Map((saved || []).map(t => [t.id, t]));
  const merged: TaskItem[] = shipped.map(def => {
    const user = savedMap.get(def.id);
    if (!user) return def;
    const shippedDone = def.status === "completed";
    return {
      ...def,
      status: shippedDone ? "completed" : user.status,
      completedAt: shippedDone ? def.completedAt || user.completedAt : user.completedAt,
      snoozedUntil: user.snoozedUntil,
      userNotes: user.userNotes || def.userNotes
    };
  });
  const shippedIds = new Set(shipped.map(t => t.id));
  for (const t of saved || []) if (!shippedIds.has(t.id)) merged.push(t); // user-created tasks
  const today = todayStr();
  return merged.map(t =>
    t.status === "snoozed" && t.snoozedUntil && t.snoozedUntil <= today
      ? { ...t, status: "pending", snoozedUntil: undefined }
      : t
  );
}

const shippedTasks = () => ((initialTasksData as { tasks: TaskItem[] }).tasks || []);

function loadSaved(): TaskItem[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TaskItem[]) : null;
  } catch {
    return null;
  }
}

type View = "pending" | "snoozed" | "completed";

export default function TasksPage() {
  const [tasks, setTasks] = useState<TaskItem[]>(() => mergeTasks(shippedTasks(), loadSaved()));
  const [view, setView] = useState<View>("pending");
  const [category, setCategory] = useState<TaskCategory>("all");
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(CHECKLIST_KEY) || "{}");
    } catch {
      return {};
    }
  });

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newCategory, setNewCategory] = useState<Exclude<TaskCategory, "all">>("manual");
  const [newPriority, setNewPriority] = useState<TaskPriority>("medium");
  const [newDue, setNewDue] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
    } catch {}
  }, [tasks]);

  useEffect(() => {
    try {
      localStorage.setItem(CHECKLIST_KEY, JSON.stringify(checked));
    } catch {}
  }, [checked]);

  const update = (id: string, patch: Partial<TaskItem>) =>
    setTasks(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));

  const complete = (id: string) => update(id, { status: "completed", completedAt: todayStr(), snoozedUntil: undefined });
  const reopen = (id: string) => update(id, { status: "pending", completedAt: undefined, snoozedUntil: undefined });
  const snooze = (id: string, days: number) => update(id, { status: "snoozed", snoozedUntil: addDays(days) });
  const remove = (id: string) => setTasks(prev => prev.filter(t => t.id !== id));

  const toggleSubtask = (taskId: string, idx: number) =>
    setChecked(prev => ({ ...prev, [`${taskId}-${idx}`]: !prev[`${taskId}-${idx}`] }));

  const reload = () => setTasks(prev => mergeTasks(shippedTasks(), prev));

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const task: TaskItem = {
      id: `manual-${Date.now()}`,
      category: newCategory,
      title: newTitle.trim(),
      description: newDesc.trim(),
      priority: newPriority,
      dueDate: newDue || undefined,
      source: "Added manually",
      status: "pending",
      createdAt: todayStr()
    };
    setTasks(prev => [task, ...prev]);
    setIsAddOpen(false);
    setNewTitle("");
    setNewDesc("");
    setNewDue("");
    setNewPriority("medium");
  };

  const counts = useMemo(() => {
    const open = tasks.filter(t => t.status === "pending");
    return {
      open: open.length,
      overdue: open.filter(t => t.dueDate && daysUntil(t.dueDate) < 0).length,
      thisWeek: open.filter(t => t.dueDate && daysUntil(t.dueDate) >= 0 && daysUntil(t.dueDate) <= 7).length,
      snoozed: tasks.filter(t => t.status === "snoozed").length,
      done: tasks.filter(t => t.status === "completed").length
    };
  }, [tasks]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tasks
      .filter(t => t.status === view)
      .filter(t => category === "all" || t.category === category)
      .filter(
        t =>
          !q ||
          [t.title, t.description, t.asset, t.provider, t.source].some(v => v?.toLowerCase().includes(q))
      )
      .sort((a, b) => {
        if (view === "completed") return (b.completedAt || "").localeCompare(a.completedAt || "");
        if (view === "snoozed") return (a.snoozedUntil || "").localeCompare(b.snoozedUntil || "");
        const da = a.dueDate ? parseDay(a.dueDate) : Infinity;
        const db = b.dueDate ? parseDay(b.dueDate) : Infinity;
        return da - db || PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      });
  }, [tasks, view, category, query]);

  // Open tasks are grouped by when they are due; other views are one list.
  const groups = useMemo(() => {
    if (view !== "pending") return [{ key: view, label: "", items: visible }];
    const buckets: Record<string, TaskItem[]> = { overdue: [], week: [], month: [], later: [], none: [] };
    for (const t of visible) {
      if (!t.dueDate) buckets.none.push(t);
      else {
        const n = daysUntil(t.dueDate);
        buckets[n < 0 ? "overdue" : n <= 7 ? "week" : n <= 30 ? "month" : "later"].push(t);
      }
    }
    return [
      { key: "overdue", label: "Overdue", items: buckets.overdue },
      { key: "week", label: "Next 7 days", items: buckets.week },
      { key: "month", label: "Next 30 days", items: buckets.month },
      { key: "later", label: "Later", items: buckets.later },
      { key: "none", label: "No due date", items: buckets.none }
    ].filter(g => g.items.length > 0);
  }, [visible, view]);

  const usedCategories = useMemo(
    () => (Object.keys(CATEGORY_LABELS) as Exclude<TaskCategory, "all">[]).filter(c => tasks.some(t => t.category === c)),
    [tasks]
  );

  const savings = (initialTasksData as any)?.metrics?.totalRealizedAnnualSavings as string | undefined;

  const tabs: { id: View; label: string; count: number }[] = [
    { id: "pending", label: "Open", count: counts.open },
    { id: "snoozed", label: "Snoozed", count: counts.snoozed },
    { id: "completed", label: "Done", count: counts.done }
  ];

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Tasks</h1>
            <p className="mt-1 text-sm text-slate-500">
              {counts.open} open
              {counts.overdue > 0 && (
                <>
                  {" · "}
                  <span className="text-rose-700">{counts.overdue} overdue</span>
                </>
              )}
              {" · "}
              {counts.thisWeek} due in the next 7 days
              {savings && <> · {savings} saved</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={reload}
              title="Load the latest task list; your progress is kept"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setIsAddOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              New task
            </button>
          </div>
        </header>

        {/* Tabs + filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-slate-200">
          <nav className="flex gap-6 -mb-px" aria-label="Task status">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={`pb-2.5 text-sm border-b-2 transition-colors ${
                  view === tab.id
                    ? "border-slate-900 text-slate-900 font-medium"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                {tab.label} <span className="text-slate-400 font-normal">{tab.count}</span>
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2 pb-2.5">
            <select
              value={category}
              onChange={e => setCategory(e.target.value as TaskCategory)}
              aria-label="Category"
              className="text-sm text-slate-700 bg-white border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="all">All categories</option>
              {usedCategories.map(c => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-44 sm:w-56 pl-8 pr-7 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Task list */}
        {visible.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-slate-700">
              {view === "pending" ? "Nothing open. Nice." : view === "snoozed" ? "No snoozed tasks." : "Nothing completed yet."}
            </p>
            {(query || category !== "all") && (
              <p className="mt-1 text-sm text-slate-500">Try clearing the search or category filter.</p>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {groups.map(group => (
              <section key={group.key}>
                {group.label && (
                  <h2
                    className={`mb-2 text-xs font-medium uppercase tracking-wide ${
                      group.key === "overdue" ? "text-rose-700" : "text-slate-500"
                    }`}
                  >
                    {group.label} <span className="text-slate-400">{group.items.length}</span>
                  </h2>
                )}
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
                  {group.items.map(task => {
                    const open = expandedId === task.id;
                    const done = task.status === "completed";
                    const due = task.dueDate && !done ? dueLabel(task.dueDate) : null;
                    const steps = task.checklist || [];
                    const stepsDone = steps.filter((_, i) => checked[`${task.id}-${i}`]).length;
                    const meta = [
                      CATEGORY_LABELS[task.category] || "General",
                      task.asset,
                      stepsDone > 0 ? `${stepsDone}/${steps.length} steps` : null,
                      task.savingsEstimate && !/n\/a/i.test(task.savingsEstimate) ? `Saves ${task.savingsEstimate}` : null
                    ].filter(Boolean);

                    return (
                      <li key={task.id} className="group">
                        <div className="flex items-start gap-3 px-4 py-3">
                          {/* Complete toggle */}
                          <button
                            onClick={() => (done ? reopen(task.id) : complete(task.id))}
                            aria-label={done ? "Mark as not done" : "Mark as done"}
                            title={done ? "Reopen" : "Mark as done"}
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                              done
                                ? "border-slate-700 bg-slate-700 text-white"
                                : "border-slate-300 text-transparent hover:border-slate-500 hover:text-slate-400"
                            }`}
                          >
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </button>

                          {/* Title + meta */}
                          <button
                            onClick={() => setExpandedId(open ? null : task.id)}
                            className="min-w-0 flex-1 text-left"
                            aria-expanded={open}
                          >
                            <div className="flex items-center gap-2">
                              {!done && (task.priority === "urgent" || task.priority === "high") && (
                                <span
                                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                    task.priority === "urgent" ? "bg-rose-500" : "bg-amber-500"
                                  }`}
                                  title={task.priority === "urgent" ? "Urgent" : "High priority"}
                                />
                              )}
                              <span
                                className={`text-sm ${done ? "text-slate-400 line-through" : "text-slate-900 font-medium"}`}
                              >
                                {task.title}
                              </span>
                            </div>
                            <div className="mt-0.5 text-xs text-slate-500">
                              {[
                                due && (
                                  <span
                                    className={
                                      due.tone === "overdue"
                                        ? "text-rose-700"
                                        : due.tone === "soon"
                                        ? "text-slate-800"
                                        : undefined
                                    }
                                  >
                                    {due.text}
                                  </span>
                                ),
                                task.status === "snoozed" && task.snoozedUntil && (
                                  <span>Snoozed until {shortDate(task.snoozedUntil)}</span>
                                ),
                                done && task.completedAt && <span>Done {shortDate(task.completedAt)}</span>,
                                ...meta.map(m => <span>{m}</span>)
                              ]
                                .filter(Boolean)
                                .map((node, i) => (
                                  <span key={i} className="inline-block whitespace-nowrap">
                                    {i > 0 && <span className="mx-1.5 text-slate-300">·</span>}
                                    {node}
                                  </span>
                                ))}
                            </div>
                          </button>

                          <button
                            onClick={() => setExpandedId(open ? null : task.id)}
                            aria-label={open ? "Hide details" : "Show details"}
                            className="mt-0.5 p-0.5 text-slate-400 hover:text-slate-700"
                          >
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </div>

                        {open && (
                          <div className="px-4 pb-4 pl-12 space-y-4">
                            {task.description && (
                              <p className="text-sm leading-relaxed text-slate-600">{task.description}</p>
                            )}

                            {steps.length > 0 && (
                              <ul className="space-y-1.5">
                                {steps.map((step, idx) => {
                                  const isChecked = !!checked[`${task.id}-${idx}`];
                                  return (
                                    <li key={idx}>
                                      <label className="flex cursor-pointer items-start gap-2.5 text-sm text-slate-700 select-none">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => toggleSubtask(task.id, idx)}
                                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-400"
                                        />
                                        <span className={isChecked ? "text-slate-400 line-through" : ""}>{step}</span>
                                      </label>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}

                            {task.links && task.links.length > 0 && (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {task.links.map((link, idx) => (
                                  <a
                                    key={idx}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-sm text-slate-700 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-700"
                                  >
                                    {link.label}
                                    <ExternalLink className="h-3 w-3 text-slate-400" />
                                  </a>
                                ))}
                              </div>
                            )}

                            <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
                              {task.status === "pending" && (
                                <>
                                  <button
                                    onClick={() => complete(task.id)}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-white hover:bg-slate-800"
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                    Mark as done
                                  </button>
                                  <button
                                    onClick={() => snooze(task.id, 7)}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                                  >
                                    <Clock className="h-3.5 w-3.5 text-slate-400" />
                                    Snooze 1 week
                                  </button>
                                  <button
                                    onClick={() => snooze(task.id, 30)}
                                    className="rounded-md border border-slate-200 px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                                  >
                                    1 month
                                  </button>
                                </>
                              )}
                              {task.status !== "pending" && (
                                <button
                                  onClick={() => reopen(task.id)}
                                  className="rounded-md border border-slate-200 px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                                >
                                  {task.status === "snoozed" ? "Wake up now" : "Reopen"}
                                </button>
                              )}
                              <span className="flex-1" />
                              {task.id.startsWith("manual-") && (
                                <button
                                  onClick={() => remove(task.id)}
                                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-slate-500 hover:bg-slate-50 hover:text-rose-700"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Delete
                                </button>
                              )}
                              <span className="text-xs text-slate-400">
                                {task.source}
                                {task.createdAt && ` · added ${shortDate(task.createdAt)}`}
                              </span>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}

        {/* New task dialog */}
        {isAddOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4"
            onClick={() => setIsAddOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-task-title"
              className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 id="new-task-title" className="text-base font-semibold text-slate-900">
                  New task
                </h3>
                <button onClick={() => setIsAddOpen(false)} aria-label="Close" className="text-slate-400 hover:text-slate-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleCreate} className="mt-4 space-y-3">
                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="What needs to be done?"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                />
                <textarea
                  rows={3}
                  placeholder="Notes (optional)"
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                />
                <div className="grid grid-cols-3 gap-2">
                  <label className="text-xs text-slate-500">
                    Due
                    <input
                      type="date"
                      value={newDue}
                      onChange={e => setNewDue(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    />
                  </label>
                  <label className="text-xs text-slate-500">
                    Priority
                    <select
                      value={newPriority}
                      onChange={e => setNewPriority(e.target.value as TaskPriority)}
                      className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Normal</option>
                      <option value="low">Low</option>
                    </select>
                  </label>
                  <label className="text-xs text-slate-500">
                    Category
                    <select
                      value={newCategory}
                      onChange={e => setNewCategory(e.target.value as Exclude<TaskCategory, "all">)}
                      className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      {(Object.keys(CATEGORY_LABELS) as Exclude<TaskCategory, "all">[]).map(c => (
                        <option key={c} value={c}>
                          {CATEGORY_LABELS[c]}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsAddOpen(false)}
                    className="rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="rounded-md bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-800">
                    Add task
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
